import { useEffect, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useAuth } from "@/app/context/AuthContext";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { canBuyCreditTopups } from "@/features/profile";
import { NikolaTrialSelector } from "./components";
import {
  openBillingPortal,
  startClubCheckout,
  startNikolaTopup,
  switchToClubYearly,
  type NikolaTopupPackId,
} from "./billing.api";
import { trackProductEvent } from "@/shared/analytics/ga4";
import { ChesslaLoadingMark } from "@/shared/components/ChesslaLoadingMark";

const until = (value: string | null): string => {
  if (!value) return "";
  const seconds = Math.max(0, Math.ceil((new Date(value).getTime() - Date.now()) / 1_000));
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  return days ? `${days}d ${hours}h` : `${hours}h ${Math.floor((seconds % 3_600) / 60)}m`;
};

export const BillingPanel = () => {
  const paymentsDisabled = import.meta.env.VITE_PAYMENTS_DISABLED === "true";
  const auth = useAuth();
  const snapshot = useEntitlementsStore((state) => state.snapshot);
  const loading = useEntitlementsStore((state) => state.loading);
  const refresh = useEntitlementsStore((state) => state.refresh);
  const [actionLoading, setActionLoading] = useState(false);
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => tick((value) => value + 1), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    if (snapshot?.membership.active && new URLSearchParams(window.location.search).get("billing") === "success")
      trackProductEvent("club_subscription_activated");
    if (
      new URLSearchParams(window.location.search).has("billing") ||
      new URLSearchParams(window.location.search).has("credits")
    )
      void refresh();
  }, [refresh, snapshot?.membership.active]);

  if (!snapshot && loading) return <ChesslaLoadingMark page="Membership" label="Checking membership" />;

  if (!snapshot)
    return (
      <Box sx={{ mt: 3, border: "2px solid", borderColor: "divider", p: 2.5 }}>
        <Typography sx={{ fontWeight: 800 }}>Membership status is temporarily unavailable.</Typography>
        <Button sx={{ mt: 1 }} onClick={() => void refresh()}>
          Retry
        </Button>
      </Box>
    );

  const active = snapshot.membership.active;
  const beta = snapshot.trial.eligible;
  const handle = async (action: () => Promise<void>) => {
    setActionLoading(true);
    try {
      await action();
    } finally {
      setActionLoading(false);
    }
  };
  const topups: Array<{ id: NikolaTopupPackId; credits: number; price: number }> = [
    { id: "credits_500", credits: 500, price: 5 },
    { id: "credits_1000", credits: 1_000, price: 10 },
    { id: "credits_2000", credits: 2_000, price: 20 },
  ];

  return (
    <Box
      data-tour="membership-overview"
      sx={{
        mt: 3,
        border: "2px solid",
        borderColor: active ? "primary.main" : "secondary.main",
        bgcolor: "background.default",
      }}
    >
      <Box
        data-tour="membership-status"
        sx={{
          p: { xs: 2, sm: 2.5 },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" },
          gap: 2,
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{ color: active ? "primary.main" : "secondary.main", fontWeight: 800, letterSpacing: ".12em" }}
          >
            {paymentsDisabled
              ? "CHESSLA / OPEN BETA"
              : active
                ? "NIKOLA'S CLUB / ACTIVE"
                : beta
                  ? "NIKOLA'S TRIAL / BETA"
                  : "NIKOLA'S CLUB"}
          </Typography>
          <Typography
            sx={{
              mt: 0.45,
              fontSize: { xs: "1.35rem", sm: "1.65rem" },
              fontWeight: 800,
              letterSpacing: "-.05em",
              lineHeight: 1,
            }}
          >
            {paymentsDisabled
              ? "Your core workspace is open."
              : active
                ? "Nikola is in your corner."
                : beta
                  ? "Read your game across one real format."
                  : "Keep Nikola at the board."}
          </Typography>
          <Typography sx={{ mt: 0.9, color: "text.secondary", maxWidth: 620, fontSize: ".78rem", lineHeight: 1.55 }}>
            {paymentsDisabled
              ? "Import games, analyze moves, and explore Patterns for free during open beta."
              : active
                ? "750 measured credits arrive each billing month, added to any free or purchased credits you already have."
                : beta
                  ? "Choose 10 games at one exact clock and use 2,500 lifetime measured beta credits."
                  : snapshot.coreOpenBeta
                    ? "Core analysis and Patterns remain free during open beta. Use your remaining signup credits with Nikola; join Club for recurring credits."
                    : "Your guided first game includes 500 Nikola credits. Join Club to continue after onboarding."}
          </Typography>
        </Box>
        {paymentsDisabled ? (
          <Box role="status">
            <Typography sx={{ fontWeight: 800 }}>Membership — Coming soon</Typography>
            <Typography sx={{ mt: 1 }}>Purchases are temporarily unavailable</Typography>
            <Typography sx={{ mt: 1, color: "text.secondary", fontSize: ".78rem" }}>
              Your existing Nikola credits remain unchanged. For billing help, contact support@chessla.net.
            </Typography>
          </Box>
        ) : active ? (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="contained"
              disabled={loading || actionLoading}
              onClick={() => void handle(openBillingPortal)}
            >
              Manage membership
            </Button>
            {snapshot.membership.plan === "club_monthly" && (
              <Button
                variant="outlined"
                disabled={actionLoading}
                onClick={() =>
                  void handle(async () => {
                    trackProductEvent("club_yearly_upgrade_started");
                    await switchToClubYearly();
                  })
                }
              >
                Move yearly · $96
              </Button>
            )}
          </Stack>
        ) : (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="contained"
              color="secondary"
              disabled={loading || actionLoading}
              onClick={() =>
                void handle(async () => {
                  trackProductEvent("club_checkout_started");
                  await startClubCheckout("club_monthly");
                })
              }
            >
              Join monthly · $12
            </Button>
            <Button
              variant="outlined"
              disabled={loading || actionLoading}
              onClick={() =>
                void handle(async () => {
                  trackProductEvent("club_checkout_started");
                  await startClubCheckout("club_yearly");
                })
              }
            >
              Join yearly · $96
            </Button>
          </Stack>
        )}
      </Box>
      {active && (
        <Box data-tour="membership-usage" sx={{ px: { xs: 2, sm: 2.5 }, pb: 2.5 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ fontSize: ".7rem", fontWeight: 800 }}>
              {snapshot.credits.available} credits available
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: ".66rem" }}>
              750 more in {until(snapshot.membership.nextGrantAt)}
            </Typography>
          </Stack>
          {!paymentsDisabled && canBuyCreditTopups(auth.profile?.username) && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
              {topups.map((pack) => (
                <Button
                  key={pack.id}
                  variant="outlined"
                  disabled={actionLoading}
                  onClick={() => void handle(() => startNikolaTopup(pack.id))}
                >
                  +{pack.credits} · ${pack.price}
                </Button>
              ))}
            </Stack>
          )}
        </Box>
      )}
      {!active && snapshot.trial.eligible && <NikolaTrialSelector />}
    </Box>
  );
};
